import { enableMapSet } from 'immer'
import { createContext, createElement, useContext, useRef } from 'react'
import type { PropsWithChildren } from 'react'
import { toast } from 'sonner'
import { createStore, useStore } from 'zustand'
import type { StorageValue } from 'zustand/middleware'
import { devtools, persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import { TProjectDetail, TScene } from '@/apis/typings'
import { ProjectControllerFindAll } from '@/apis/v1/project'
import { SceneControllerGetScene } from '@/apis/v1/scene'
import { UserControllerGetProfile } from '@/apis/v1/user'

import type { Selector } from './type'

enableMapSet()

interface RootStoreProps {}

interface RootStoreState extends RootStoreProps {
  loading: boolean

  projects: TProjectDetail[]
  currentProject: TProjectDetail | undefined
  userInfo: Definitions.UserWithProfile | undefined
  addProjectModalVisible: boolean
  avatarUpdatedAt: string

  scenes: TScene[] | undefined

  init(): void

  updateUserInfo(): void

  updateProjects(): Promise<void>

  updateProject(project: TProjectDetail): void

  changeAddProjectModal(open: boolean): void

  setCurrentProject(project: TProjectDetail): void

  reset: () => void

  showSuccess: (text: string | React.ReactNode) => void
  showInfo: (text: string | React.ReactNode) => void
  showError: (text: string | React.ReactNode) => void
}

type RootStore = ReturnType<typeof createRootStore>

/**
 * Context
 */
const rootContext = createContext<RootStore | null>(null)

/**
 * 创建根级仓库
 * @param {Partial<RootStoreProps>} initialProps
 * @returns {RootStore}
 */
function createRootStore(initialProps?: Partial<RootStoreProps>) {
  return createStore<RootStoreState>()(
    persist(
      immer(
        devtools((set, get, store) => ({
          loading: true,
          addProjectModalVisible: false,
          scenes: undefined,
          currentProject: undefined,
          avatarUpdatedAt: '',
          userInfo: undefined,
          projects: [],
          ...initialProps,

          init: async () => {
            const userInfo = get().userInfo
            if (userInfo?._id) {
              return
            }

            const [userInfoRes, sceneInfoRes, projectInfoRes] =
              await Promise.all([
                UserControllerGetProfile({}),
                SceneControllerGetScene({}),
                ProjectControllerFindAll({}),
              ])
            set((state) => {
              state.userInfo = userInfoRes.data
              state.loading = false
              state.scenes = sceneInfoRes.data
              state.projects = projectInfoRes.data
              state.currentProject = projectInfoRes.data[0]
            })
          },

          updateProjects: async () => {
            const projects = await ProjectControllerFindAll({})
            set((state) => {
              state.projects = projects.data || []
            })
          },

          setCurrentProject(project: TProjectDetail) {
            set((state) => {
              state.currentProject = project
            })
          },

          updateProject: (currentProject: TProjectDetail) => {
            // const currentProjectId =
            //   slug || localStorage.getItem('currentProjectId')
            // if (currentProjectId === get().currentProject?.appid) {
            //   return
            // }
            // set((state) => {
            //   state.c = currentProjectId
            //   localStorage.setItem('currentProjectId', currentProjectId as string)
            //   window.location.reload()
            // })
          },

          updateUserInfo: async () => {
            const userInfoRes = await UserControllerGetProfile({})
            set((state) => {
              state.userInfo = userInfoRes.data
              state.avatarUpdatedAt = new Date().toISOString()
            })
          },
          changeAddProjectModal: (open: boolean) => {
            set((state) => {
              state.addProjectModalVisible = open
            })
          },
          // 重置状态
          reset: () =>
            set((state) => {
              // state.pictureBookId = null
              // state.token = null
              // state.userInfo = null
              // state.loginModalVisible = false
            }),

          showSuccess: (text: string | React.ReactNode) => {
            toast.success(text)
          },

          showError: (text: string | React.ReactNode) => {
            toast.error(text)
          },

          showInfo: (text: string | React.ReactNode) => {
            toast.info(text)
          },
        })),
      ),
      {
        name: 'root-storage',
        storage: {
          getItem: (name) => {
            const stringValue = localStorage.getItem(name)
            if (!stringValue) return null
            const { state } = JSON.parse(
              stringValue,
            ) as StorageValue<RootStoreProps>
            return {
              state: {
                ...state,
              },
            }
          },
          setItem: (name, newValue: StorageValue<RootStoreProps>) => {
            const stringValue = JSON.stringify({
              state: {
                ...newValue.state,
              },
            })
            localStorage.setItem(name, stringValue)
          },
          removeItem: (name) => localStorage.removeItem(name),
        },
      },
    ),
  )
}

/**
 * Provider
 * @description 上下文插件
 * @param {PropsWithChildren<RootStoreProps>}
 * @returns {JSX.Element}
 */
const RootProvider = ({
  children,
  ...props
}: PropsWithChildren<Partial<RootStoreProps>>) => {
  const storeRef = useRef<RootStore>()
  if (!storeRef.current) {
    storeRef.current = createRootStore(props)
  }
  return createElement(
    rootContext.Provider,
    { value: storeRef.current },
    children,
  )
}

/**
 * Hook
 * @description 获取上下文
 * @param {Selector} selector
 * @returns {RootStore}
 */
function useModel<T>(selector: Selector<RootStoreState, T>): T {
  const store = useContext(rootContext)
  if (!store) throw new Error('useModel must be used within a RootProvider')
  return useStore(store, selector)
}

export { RootProvider, useModel }
export type { RootStoreState }
